const STORAGE_PREFIX = "layup:review-encouragement";
const REVIEW_POSTED_PREFIX = "layup:review-posted";
const PENDING_REVIEW_PREFIX = "layup:review:pending:";
const CLICK_THRESHOLD = 10;
const PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

interface ReviewEncouragementState {
  interactionCount: number;
  lastPromptAt: string | null;
}

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function encouragementKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

function reviewPostedKey(userId: string): string {
  return `${REVIEW_POSTED_PREFIX}:${userId}`;
}

function readState(userId: string): ReviewEncouragementState {
  if (!canUseStorage()) {
    return {
      interactionCount: 0,
      lastPromptAt: null,
    };
  }

  try {
    const raw = window.localStorage.getItem(encouragementKey(userId));
    if (!raw) {
      return {
        interactionCount: 0,
        lastPromptAt: null,
      };
    }

    const parsed = JSON.parse(raw) as Partial<ReviewEncouragementState>;
    return {
      interactionCount: typeof parsed.interactionCount === "number" ? parsed.interactionCount : 0,
      lastPromptAt: typeof parsed.lastPromptAt === "string" ? parsed.lastPromptAt : null,
    };
  } catch {
    return {
      interactionCount: 0,
      lastPromptAt: null,
    };
  }
}

function writeState(userId: string, state: ReviewEncouragementState): void {
  if (!canUseStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(encouragementKey(userId), JSON.stringify(state));
  } catch {
    // Ignore local-only prompt storage failures.
  }
}

function hasPendingReviewEvidence(userId: string): boolean {
  if (!canUseStorage()) {
    return false;
  }

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(`${PENDING_REVIEW_PREFIX}${userId}:`)) {
      return true;
    }
  }

  return false;
}

export function hasPostedReviewLocally(userId: string | undefined): boolean {
  if (!userId || !canUseStorage()) {
    return false;
  }

  return window.localStorage.getItem(reviewPostedKey(userId)) === "1" || hasPendingReviewEvidence(userId);
}

export function markUserHasPostedReview(userId: string | undefined): void {
  if (!userId || !canUseStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(reviewPostedKey(userId), "1");
  } catch {
    // Ignore local-only prompt storage failures.
  }
}

export function recordReviewEncouragementInteraction(userId: string | undefined): boolean {
  if (!userId || !canUseStorage() || hasPostedReviewLocally(userId)) {
    return false;
  }

  const state = readState(userId);
  const now = Date.now();
  const lastPromptAt = state.lastPromptAt ? new Date(state.lastPromptAt).getTime() : Number.NaN;
  const nextState: ReviewEncouragementState = {
    interactionCount: state.interactionCount + 1,
    lastPromptAt: state.lastPromptAt,
  };

  if (
    nextState.interactionCount >= CLICK_THRESHOLD
    && (!Number.isFinite(lastPromptAt) || now - lastPromptAt >= PROMPT_COOLDOWN_MS)
  ) {
    writeState(userId, {
      interactionCount: 0,
      lastPromptAt: new Date(now).toISOString(),
    });
    return true;
  }

  writeState(userId, nextState);
  return false;
}

export function dismissReviewEncouragement(userId: string | undefined): void {
  if (!userId || !canUseStorage()) {
    return;
  }

  const state = readState(userId);
  writeState(userId, {
    interactionCount: state.interactionCount,
    lastPromptAt: new Date().toISOString(),
  });
}
